import React, { FC, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import axiosInstant from "../../../../../api";
import { AnalysisScheduleFields } from "../../../../../components/Analysis/AnalysisScheduleFields";
import { updateProductPdpUrl } from "../../../../../api/endpoints";
import { SkuPdpLinkEditor } from "../../../../../components/SkuLinks/SkuPdpLinkEditor";
import { apiErrorCode } from "../../../../../utils/apiError";
import { INTERVAL_LABELS, PDP_SCORE_INTERVALS } from "../../../../../utils/plans";
import { isSessionRedirect } from "../pageModel";
import { useMountedRef } from "../components/useMountedRef";
import { PAGE_URL_NOT_HTTP, intervalErrorMessage } from "./pageModel";
import type { PdpUrl } from "./pageModel";

/**
 * "Edit URL, SKUs and refresh" — the modal's edit dialog, copied: the markup
 * of MODAL/ProductPdpUrlsPanel.tsx:817-870, `openEdit` (:435-441) and
 * `handleSaveEdit` (:443-474), line for line. Same PATCH, same body
 * ({ pdp_url, skus, score_interval? }), same inline errors, same toast.
 * Kept as the modal draws it (MUI Dialog + TextField) rather than redrawn:
 * the handoff doesn't design it, and a copy can't drift in behaviour.
 *
 * What differs is after the save. The modal re-reads with `fetchRows`, which
 * swaps the panel for its loader. Here `onSaved` hands the section the saved
 * values: it patches the row at once (useProductPages' `patchRow`) and
 * re-reads behind, not awaited. So "Saving…" ends and the dialog closes when
 * the PATCH answers, and reopening Edit straight away shows what was saved,
 * not the row as it was before (review #1). The dialog fills its fields when
 * it opens, from the row it was opened on, so a re-read landing while it is
 * open never resets a field mid-edit.
 *
 * A session redirect (a 401, or the expired-token cancel — `isSessionRedirect`)
 * changes nothing: no inline error, "Saving…" holds while the browser leaves
 * for the login page, as the section's own writes do.
 */

interface Props {
  productId: number;
  /** The page being edited; null = closed. */
  row: PdpUrl | null;
  onClose: () => void;
  /**
   * After a successful save, with the saved values (and whatever the PATCH
   * answered): the section patches the row and re-reads the list in the
   * background (a changed URL regenerates JSON-LD server-side).
   */
  onSaved: (id: number, saved: Partial<PdpUrl>) => void;
}

export const EditPageDialog: FC<Props> = ({ productId, row, onClose, onSaved }) => {
  const [editUrl, setEditUrl] = useState("");
  const [editInterval, setEditInterval] = useState("");
  const [editWeekday, setEditWeekday] = useState<number | null>(null);
  const [editDayOfMonth, setEditDayOfMonth] = useState<number | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSkus, setEditSkus] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const mounted = useMountedRef();

  // openEdit (:435-441): the dialog opens on the row's current values.
  useEffect(() => {
    if (!row) return;
    setEditUrl(row.pdp_url);
    setEditInterval(row.score_interval || "");
    setEditWeekday(row.score_weekday === undefined ? null : row.score_weekday);
    setEditDayOfMonth(row.score_day_of_month === undefined ? null : row.score_day_of_month);
    setEditSkus(row.skus || []);
    setEditError(null);
  }, [row]);

  const handleSaveEdit = async () => {
    if (!row) return;
    const trimmed = editUrl.trim();
    // The modal's own test (:446), looser than the Add form's (`addPageUrlError`
    // also wants a host); the same message, PAGE_URL_NOT_HTTP.
    if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
      setEditError(PAGE_URL_NOT_HTTP);
      return;
    }
    setSavingEdit(true);
    setEditError(null);
    const { id } = row;
    try {
      const { data } = await axiosInstant.patch(updateProductPdpUrl(productId, id), {
        pdp_url: trimmed,
        skus: editSkus,
        ...(editInterval ? { score_interval: editInterval } : {}),
        ...(editWeekday !== null ? { score_weekday: editWeekday } : {}),
        ...(editDayOfMonth !== null ? { score_day_of_month: editDayOfMonth } : {}),
      });
      toast.success("PDP URL updated.");
      // The PATCH answers the saved row (desirelist-api pdpUrls.js updatePdpUrl →
      // withSkus); only the three fields this dialog edits are taken from it,
      // falling back to what was sent. Default ("") leaves the cadence as it was.
      const body = data?.id != null ? data : data?.data;
      const saved: Partial<PdpUrl> = {
        pdp_url: typeof body?.pdp_url === "string" ? body.pdp_url : trimmed,
        skus: Array.isArray(body?.skus) ? body.skus : editSkus,
      };
      if (body?.score_interval !== undefined) saved.score_interval = body.score_interval;
      else if (editInterval) saved.score_interval = editInterval;
      if (body?.score_weekday !== undefined) saved.score_weekday = body.score_weekday;
      if (body?.score_day_of_month !== undefined) saved.score_day_of_month = body.score_day_of_month;
      onSaved(id, saved);
      if (!mounted.current) return;
      setSavingEdit(false);
      onClose();
    } catch (e: any) {
      if (isSessionRedirect(e)) return;
      if (!mounted.current) return;
      const intervalError = intervalErrorMessage(e);
      if (e?.response?.status === 409 || apiErrorCode(e) === "PdpUrlAlreadyExists") {
        setEditError("That URL is already on this product.");
      } else if (intervalError) {
        setEditError(intervalError);
      } else {
        setEditError("Couldn't save the PDP URL. Please try again.");
      }
      setSavingEdit(false);
    }
  };

  return (
    <Dialog open={!!row} onClose={savingEdit ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Edit PDP URL & SKUs
        <IconButton aria-label="Close" onClick={onClose} size="small" disabled={savingEdit}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="PDP URL"
          placeholder="https://retailer.com/p/product…"
          value={editUrl}
          onChange={(e) => setEditUrl(e.target.value)}
          error={!!editError}
          helperText={editError || "Changing the URL regenerates the JSON-LD for this page."}
          sx={{ mt: 1, mb: 2 }}
        />
        <AnalysisScheduleFields
          label="PDP analysis interval"
          value={{ interval: editInterval, weekday: editWeekday, dayOfMonth: editDayOfMonth }}
          onChange={(next) => {
            setEditInterval(next.interval);
            setEditWeekday(next.weekday);
            setEditDayOfMonth(next.dayOfMonth);
          }}
          helperText="How often this page is analyzed while on Autopilot. Capped by the brand's plan."
          disabled={savingEdit}
          className="mb-5"
        />
        {/* SKUs save on the same PATCH as the URL — editing them never
            touches the row's scores or generated JSON-LD. */}
        <SkuPdpLinkEditor value={editSkus} onChange={setEditSkus} disabled={savingEdit} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={savingEdit}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={handleSaveEdit} disabled={savingEdit}>
          {savingEdit ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

